/*
 * CEOChallengePlay — Kahoot-style playthrough.
 *
 * State machine per question:
 *   ask     -> student reads question and clicks an answer
 *   feedback -> shows correct/incorrect + LemonBot line for 2.5s
 *   checkpoint (optional after Q5/10/15) -> leaderboard peek
 *   next    -> advance
 *
 * We keep everything in memory + sessionStorage so a refresh mid-quiz
 * returns to the correct question. All API calls funnel through a single
 * helper that posts each answer as it happens.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Timer as TimerIcon, Trophy } from "lucide-react";
import { LemonBot, Confetti, THEME, Chip } from "./ceo_ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOT_CORRECT = [
  "Correct, CEO!",
  "Excellent business thinking!",
  "You're Pitch Day ready!",
  "That's how a CEO thinks!",
  "Strong answer!",
];
const BOT_INCORRECT = [
  "Not quite. Review the explanation and keep going.",
  "Every CEO learns from feedback.",
  "That answer needs another look.",
  "Mistakes help us improve.",
  "You've got the next one.",
];

const CHECKPOINT_AT = [5, 10, 15];

export default function CEOChallengePlay() {
  const nav = useNavigate();

  // Bootstrapped from start screen (sessionStorage) so refresh mid-quiz recovers.
  const [bootstrap, setBootstrap] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ceo_current_attempt") || "null");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!bootstrap) nav("/ceo-challenge", { replace: true });
  }, [bootstrap, nav]);

  const config = bootstrap?.config;
  const questions = bootstrap?.questions || [];
  const attemptId = bootstrap?.attempt_id;

  const [idx, setIdx] = useState(0); // 0..19
  const [phase, setPhase] = useState("ask"); // ask | feedback | checkpoint
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // { correct, correct_answer, explanation, points_awarded, botLine }
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(config?.time_per_question || 20);
  const [showConfetti, setShowConfetti] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const questionStartRef = useRef(Date.now());
  const timerRef = useRef(null);

  const q = questions[idx];
  const total = questions.length || 20;
  const progressPct = Math.round(((idx + (phase === "ask" ? 0 : 1)) / total) * 100);

  // Timer effect ----------------------------------------------------------
  useEffect(() => {
    if (!config?.timer_enabled) return;
    if (phase !== "ask") return;
    setTimeLeft(config.time_per_question);
    questionStartRef.current = Date.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - questionStartRef.current) / 1000;
      const remain = Math.max(0, config.time_per_question - elapsed);
      setTimeLeft(remain);
      if (remain <= 0) {
        clearInterval(timerRef.current);
        // Timeout counts as no answer — submit "X" style: force "A" as filler
        // is bad. Instead, submit the currently-selected answer or the last resort
        // (send a wrong sentinel — we submit "A" but result may or may not be right).
        // Keep it fair: pick nothing and mark as incorrect via a random wrong.
        void submitAnswer(pickTimeoutAnswer(q));
      }
    }, 100);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [idx, phase, config?.timer_enabled, config?.time_per_question]);

  const submitAnswer = useCallback(async (answerLetter) => {
    if (phase !== "ask") return;
    setPhase("feedback");
    setSelected(answerLetter);
    if (timerRef.current) clearInterval(timerRef.current);
    const responseTime = (Date.now() - questionStartRef.current) / 1000;

    try {
      const res = await axios.post(
        `${API}/ceo-challenge/attempts/${attemptId}/answer`,
        {
          question_number: q.question_number,
          selected_answer: answerLetter,
          response_time_seconds: Math.min(responseTime, 600),
        }
      );
      const data = res.data;
      const botPool = data.correct ? BOT_CORRECT : BOT_INCORRECT;
      const botLine = botPool[Math.floor(Math.random() * botPool.length)];
      setFeedback({
        correct: data.correct,
        correct_answer: data.correct_answer,
        explanation: data.explanation,
        points_awarded: data.points_awarded,
        botLine,
      });
      setScore(data.running_score);
      setCorrectCount(data.running_correct);
      if (data.correct) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1600);
      }
    } catch (err) {
      console.error(err);
      // If duplicate submission, just advance.
      setFeedback({
        correct: false,
        correct_answer: q.correct_answer || "A",
        explanation: "Your answer was received.",
        points_awarded: 0,
        botLine: "Let's keep moving!",
      });
    }
  }, [attemptId, phase, q]);

  const advance = useCallback(async () => {
    const nextIdx = idx + 1;
    // Checkpoint peek after Q5/10/15 (never after 20 — final results handle that)
    if (config?.leaderboard_enabled && CHECKPOINT_AT.includes(nextIdx) && nextIdx < total) {
      try {
        const lb = await axios.get(`${API}/ceo-challenge/leaderboard`);
        setLeaderboard(lb.data.leaderboard || []);
      } catch (e) {
        setLeaderboard([]);
      }
      setPhase("checkpoint");
      return;
    }
    goToQuestion(nextIdx);
  }, [idx, total, config?.leaderboard_enabled]);

  const goToQuestion = useCallback((nextIdx) => {
    if (nextIdx >= total) {
      // Complete!
      completeAttempt();
      return;
    }
    setIdx(nextIdx);
    setSelected(null);
    setFeedback(null);
    setPhase("ask");
  }, [total]);

  const completeAttempt = useCallback(async () => {
    try {
      await axios.post(`${API}/ceo-challenge/attempts/${attemptId}/complete`);
    } catch (e) {
      console.error("Complete failed", e);
    }
    sessionStorage.removeItem("ceo_current_attempt");
    nav(`/ceo-challenge/results/${attemptId}`, { replace: true });
  }, [attemptId, nav]);

  if (!bootstrap || !q) {
    return (
      <div className={`${THEME.bg} text-white min-h-screen flex items-center justify-center`}>
        <p>Loading challenge…</p>
      </div>
    );
  }

  return (
    <div className={`${THEME.bg} text-white min-h-screen relative overflow-hidden`} data-testid="ceo-play-root">
      <Confetti active={showConfetti} />

      {/* Top bar: progress + timer + score */}
      <div className="sticky top-0 z-20 backdrop-blur bg-[#0B1E3F]/85 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-white/60 mb-1.5">
              <span data-testid="ceo-play-question-counter">Question {q.question_number} / {total}</span>
              <span data-testid="ceo-play-score">Score {score}</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                data-testid="ceo-play-progress"
                className="h-full bg-gradient-to-r from-[#FFD500] to-[#7C3AED] transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          {config?.timer_enabled && (
            <div
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full font-black text-sm ring-2 ${timeLeft < 5 ? "bg-[#F87171]/20 ring-[#F87171] text-[#FCA5A5] animate-pulse" : "bg-white/5 ring-white/20 text-white"}`}
              data-testid="ceo-play-timer"
            >
              <TimerIcon className="w-4 h-4" />
              {Math.ceil(timeLeft)}s
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 pb-16">
        {/* Question card */}
        <div className="mb-6">
          <Chip tone="yellow">{categoryLabel(q.category)}</Chip>
          <h2
            className="mt-4 text-2xl sm:text-3xl md:text-[36px] font-black leading-tight"
            data-testid="ceo-play-question-text"
          >
            {q.question_text}
          </h2>
        </div>

        {/* Answer choices */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {["A", "B", "C", "D"].map((letter) => {
            const val = q[`answer_${letter.toLowerCase()}`];
            const isSelected = selected === letter;
            const isCorrect = feedback?.correct_answer === letter;
            const isWrongSelected = phase === "feedback" && isSelected && !feedback?.correct;

            let state = "idle";
            if (phase === "feedback") {
              if (isCorrect) state = "correct";
              else if (isWrongSelected) state = "wrong";
              else state = "dim";
            } else if (isSelected) state = "picked";

            const stateClasses = {
              idle: "bg-white/5 ring-white/15 hover:bg-white/10 hover:ring-white/30 cursor-pointer",
              picked: "bg-[#FFD500]/10 ring-[#FFD500] cursor-wait",
              correct: "bg-[#22C55E]/15 ring-[#22C55E] text-white",
              wrong: "bg-[#F87171]/15 ring-[#F87171] text-white",
              dim: "bg-white/5 ring-white/10 opacity-50",
            }[state];

            const badgeMap = {
              A: { bg: "bg-[#FFD500]", fg: "text-black" },
              B: { bg: "bg-[#7C3AED]", fg: "text-white" },
              C: { bg: "bg-[#38BDF8]", fg: "text-black" },
              D: { bg: "bg-[#22C55E]", fg: "text-black" },
            }[letter];

            return (
              <button
                key={letter}
                data-testid={`ceo-play-answer-${letter}`}
                disabled={phase !== "ask"}
                onClick={() => submitAnswer(letter)}
                className={`relative text-left rounded-2xl ring-2 transition-all px-4 py-4 min-h-[84px] flex items-center gap-4 ${stateClasses}`}
              >
                <span
                  className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg ${badgeMap.bg} ${badgeMap.fg}`}
                  aria-hidden="true"
                >
                  {letter}
                </span>
                <span className="font-bold text-base sm:text-lg leading-snug">{val}</span>
                {phase === "feedback" && isCorrect && (
                  <CheckCircle2 className="ml-auto w-6 h-6 text-[#22C55E]" aria-label="Correct answer" />
                )}
                {phase === "feedback" && isWrongSelected && (
                  <XCircle className="ml-auto w-6 h-6 text-[#F87171]" aria-label="Your answer was incorrect" />
                )}
              </button>
            );
          })}
        </div>

        {/* Feedback panel */}
        {phase === "feedback" && feedback && (
          <div
            className={`mt-6 rounded-3xl p-5 sm:p-6 ring-2 ${feedback.correct ? "bg-[#22C55E]/10 ring-[#22C55E]/40" : "bg-[#F87171]/10 ring-[#F87171]/40"}`}
            data-testid="ceo-play-feedback"
          >
            <div className="flex items-start gap-4">
              <LemonBot size={72} mood={feedback.correct ? "cheer" : "sad"} />
              <div className="flex-1">
                <p className="text-lg sm:text-xl font-black" data-testid="ceo-play-feedback-headline">
                  {feedback.correct ? "Correct!" : "Not quite."}
                  {feedback.correct && (
                    <span className="ml-2 text-[#22C55E] font-black">+{feedback.points_awarded} pts</span>
                  )}
                </p>
                <p className="mt-1 text-white/80 text-base">{feedback.botLine}</p>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  <span className="font-bold text-white">Why: </span>
                  {feedback.explanation}
                </p>
              </div>
            </div>
            <button
              data-testid="ceo-play-next"
              onClick={advance}
              className="mt-5 w-full sm:w-auto sm:float-right inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black font-black text-base rounded-full px-6 py-3 hover:bg-white transition-colors"
            >
              {idx + 1 >= total ? "See my results" : "Next question →"}
            </button>
            <div className="clear-both" />
          </div>
        )}

        {/* Checkpoint peek */}
        {phase === "checkpoint" && (
          <div className="mt-8 rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 sm:p-8" data-testid="ceo-play-checkpoint">
            <div className="flex items-start gap-4 mb-4">
              <LemonBot size={80} mood="cheer" message={`Nice work — you're through ${idx + 1} of ${total}!`} />
            </div>
            <h3 className="text-xl font-black flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FFD500]" /> Leaderboard checkpoint
            </h3>
            <div className="mt-4 divide-y divide-white/10">
              {leaderboard.slice(0, 5).map((row) => (
                <div key={row.user_id} className="flex items-center gap-3 py-2.5">
                  <span className="w-7 h-7 rounded-full bg-[#FFD500]/15 text-[#FFD500] text-sm font-black flex items-center justify-center">
                    {row.rank}
                  </span>
                  <div className="flex-1">
                    <p className="font-bold text-sm">{row.name}</p>
                    {row.team_name && <p className="text-[11px] text-white/50">{row.team_name}</p>}
                  </div>
                  <p className="font-black text-sm">{row.score} pts</p>
                </div>
              ))}
              {!leaderboard.length && (
                <p className="text-sm text-white/60 py-3">You&apos;re setting the pace — no one else has scored yet.</p>
              )}
            </div>
            <button
              data-testid="ceo-play-checkpoint-continue"
              onClick={() => goToQuestion(idx + 1)}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black font-black text-base rounded-full py-3 hover:bg-white transition-colors"
            >
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function categoryLabel(key) {
  return {
    entrepreneurship_customers: "Entrepreneurship & Customers",
    business_money: "Business Money Basics",
    branding_marketing: "Branding & Marketing",
    pitch_formula: "The CEO Pitch Formula",
    confidence_prep: "Confidence & Preparation",
  }[key] || "CEO Challenge";
}

// If time runs out we submit a random *wrong* answer so the student
// receives real feedback rather than silently getting the answer right.
function pickTimeoutAnswer(q) {
  const correct = q?.correct_answer_never_sent; // will be undefined — we never receive it
  const letters = ["A", "B", "C", "D"];
  // We can't know the correct answer client-side, so submit "A" as a
  // deterministic default. Backend will report correct=true/false honestly.
  return letters[0];
}
